import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './Calendar.css';
import './RoomSelectionSchedule.css';
import toast, { Toaster } from 'react-hot-toast';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  generateRoomId,
  generateCandidateLink,
  generateInterviewerLink,
} from '../utils/roomUtils';
import * as mammoth from 'mammoth';
import { useAlert } from './CustomAlert/AlertContext';
import { API_BASE } from '../utils/config';
import { trackEvent } from '../utils/analytics';

// MySQL returns datetime strings without 'Z', so we force UTC parsing
// to ensure correct local-time display (e.g. 2:44 PM IST not 9:14 AM)
const parseUTC = (dateStr) => {
  if (!dateStr) return new Date();
  // Dates are stored as local time strings ('YYYY-MM-DD HH:mm:ss')
  // Replace space with T and remove 'Z' to force exact local timezone parsing
  let str = String(dateStr).trim().replace(' ', 'T');
  if (str.endsWith('Z')) str = str.slice(0, -1);
  return new Date(str);
};


const RoomSelection = ({ hideNavbar = false, defaultStep = 'main' }) => {
  const { showConfirm } = useAlert();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(location.state?.step || defaultStep || 'main');

  useEffect(() => {
    if (defaultStep) {
      setCurrentStep(defaultStep);
    }
  }, [defaultStep]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [generatedRoomId, setGeneratedRoomId] = useState('');
  const [candidateLink, setCandidateLink] = useState('');
  const [interviewerLink, setInterviewerLink] = useState('');
  const [candidateLinkCopied, setCandidateLinkCopied] = useState(false);
  const [interviewerLinkCopied, setInterviewerLinkCopied] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [savedToCalendar, setSavedToCalendar] = useState(false);

  // stats state
  const [todayInterviews, setTodayInterviews] = useState(0);
  const [pendingInvites, setPendingInvites] = useState(0);
  const [totalInterviews, setTotalInterviews] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);

  // NEW: List of all interviews for "Total Created" modal
  const [totalInterviewsList, setTotalInterviewsList] = useState([]);

  // NEW: List of TODAY's interviews
  const [todaysInterviewsList, setTodaysInterviewsList] = useState([]);

  // NEW: List of pending interviews for deletion
  const [pendingInterviewsList, setPendingInterviewsList] = useState([]);
  const [activeOverview, setActiveOverview] = useState('today');
  const [showOverviewModal, setShowOverviewModal] = useState(false);
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);

  // NEW: Logout Button Hover State
  const [isLogoutHovered, setIsLogoutHovered] = useState(false);

  // NEW: Candidate Selection State
  const [candidateName, setCandidateName] = useState('');
  const [inviteStatus, setInviteStatus] = useState('');
  const [selectedResume, setSelectedResume] = useState(null);

  // NEW TIME STATE
  const [selectedTime, setSelectedTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [candidateEmail, setCandidateEmail] = useState('');
  const [interviewType, setInterviewType] = useState('AI');
  const [preDefinedQuestions, setPreDefinedQuestions] = useState([]);
  const [newQuestionInput, setNewQuestionInput] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [timePickerMode, setTimePickerMode] = useState('hour');
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [isParsingDocx, setIsParsingDocx] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [formErrors, setFormErrors] = useState({
    candidateName: '',
    candidateEmail: '',
    resume: '',
  });
  
  // NEW DOMAIN STATE
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [showOtherDomainInput, setShowOtherDomainInput] = useState(false);
  const [customDomainInput, setCustomDomainInput] = useState('');
  const domainOptions = ['Python', 'Java', 'Node.js', 'React', 'Angular', 'AWS', 'GCP', 'Azure', 'Fullstack', 'Frontend', 'Backend', 'DevOps'];
  const datePickerRef = useRef(null);
  const timePickerRef = useRef(null);
  const clockRef = useRef(null);
  const isDraggingClock = useRef(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userId = user.id || null;
  const userName = user.full_name || '';
  const isValidUser = !!userId && !!userName;

  const fetchStats = React.useCallback(async () => {
    if (!isValidUser) return;
    try {
      setStatsLoading(true);
      const res = await fetch(
        `${API_BASE}/api/auth/interview-rooms/stats/${userId}`
      );
      if (!res.ok) {
        console.error('Stats API not ok', res.status);
        return;
      }
      const data = await res.json();
      console.log('HR stats response:', data);

      setTodayInterviews(data.interviews_today || 0);
      setPendingInvites(data.pending_invites || 0);
      setTotalInterviews(data.total_interviews || 0);

      if (data.pending_list) {
        setPendingInterviewsList(data.pending_list);
      }

      if (data.total_list) {
        setTotalInterviewsList(data.total_list);
      }

      // ✅ NEW: Populate today's list
      if (data.todays_list) {
        setTodaysInterviewsList(data.todays_list);
      }

    } catch (err) {
      console.error('Error fetching HR stats', err);
    } finally {
      setStatsLoading(false);
    }
  }, [isValidUser, userId]);

  // fetch HR stats for card (based on created_by_hr_id)
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // No longer fetching candidates, HR directly types them in

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }

      if (timePickerRef.current && !timePickerRef.current.contains(event.target)) {
        setShowTimePicker(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const to12HourParts = (value) => {
    const [h = '09', m = '00'] = String(value || '09:00').split(':');
    const hour24 = Number(h);
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 || 12;
    return {
      hour: String(hour12).padStart(2, '0'),
      minute: String(m).padStart(2, '0'),
      period,
    };
  };

  const to24Hour = (hour, minute, period) => {
    let h = Number(hour);
    if (period === 'AM' && h === 12) h = 0;
    if (period === 'PM' && h !== 12) h += 12;
    return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  };

  const handleCreateNewRoom = () => {
    setCurrentStep('calendar');
  };

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

  const validateScheduleForm = () => {
    const errors = {
      candidateName: candidateName.trim() ? '' : 'Candidate name is required',
      candidateEmail: candidateEmail.trim()
        ? (isValidEmail(candidateEmail) ? '' : 'Please enter a valid email address')
        : 'Candidate email is required',
      resume: '',
    };

    setFormErrors(errors);
    return !errors.candidateName && !errors.candidateEmail;
  };

  const handleScheduleInterview = async () => {
    if (!validateScheduleForm()) {
      toast.error('Please correct the highlighted fields.');
      return;
    }

    if (!selectedDate || !selectedTime) {
      toast.error('Please select a valid date and time.');
      return;
    }

    setIsScheduling(true);

    // Combine Date and Time
    const [hours, minutes] = selectedTime.split(':');
    const finalDate = new Date(selectedDate);
    finalDate.setHours(parseInt(hours, 10));
    finalDate.setMinutes(parseInt(minutes, 10));
    finalDate.setSeconds(0);

    const newRoomId = generateRoomId();
    const candLink = interviewType === 'AI'
      ? `${window.location.origin}/ai-interview/${newRoomId}`
      : generateCandidateLink(newRoomId);
    const intLink = interviewType === 'AI' ? "" : generateInterviewerLink(newRoomId);

    setGeneratedRoomId(newRoomId);
    setCandidateLink(candLink);
    setInterviewerLink(intLink);

    // Auto-save the interview instantly
    const saved = await handleSaveToDatabase({
      roomId: newRoomId,
      selectedDate: finalDate,
      candidateLink: candLink,
      interviewerLink: intLink
    });

    if (saved) {
      if (interviewType === 'AI' && selectedResume) {
        const formData = new FormData();
        formData.append('resume', selectedResume);
        try {
          const res = await fetch(`${API_BASE}/api/auth/interview-rooms/${newRoomId}/upload-resume`, {
            method: 'POST',
            body: formData
          });
          if (!res.ok) {
            toast.error('Failed to parse the uploaded resume. The interview was created, but AI might lack context.');
          }
        } catch (err) {
          toast.error('Network error uploading resume.');
        }
      }
      setCurrentStep('links');
    }
    setIsScheduling(false);
  };

  const copyCandidateLink = () => {
    navigator.clipboard.writeText(candidateLink).then(() => {
      setCandidateLinkCopied(true);
      setTimeout(() => setCandidateLinkCopied(false), 2500);
    });
  };

  const copyInterviewerLink = () => {
    navigator.clipboard.writeText(interviewerLink).then(() => {
      setInterviewerLinkCopied(true);
      setTimeout(() => setInterviewerLinkCopied(false), 2500);
    });
  };

  const handleCopyLink = (link, label, id) => {
    if (!link) return;
    navigator.clipboard.writeText(link).then(() => {
      if (id) {
        setCopiedLinkId(id);
        setTimeout(() => setCopiedLinkId(null), 2500);
      } else {
        toast.success(`${label} Link copied!`, {
          icon: '📋',
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          },
        });
      }
    });
  };

  const handleSaveToDatabase = async (overrides = null) => {
    const interviewData = {
      roomId: overrides?.roomId || generatedRoomId,
      scheduledDate: (() => {
        const d = overrides?.selectedDate || selectedDate;
        const pad = n => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      })(),
      candidateLink: overrides?.candidateLink || candidateLink,
      interviewerLink: overrides?.interviewerLink || interviewerLink,
      status: 'SCHEDULED',
      createdByHrId: userId,
      interviewType: interviewType,
      candidateName: candidateName.trim(),
      candidateEmail: candidateEmail.trim(),
      preDefinedQuestions: preDefinedQuestions,
      domain: selectedDomains.length > 0 ? selectedDomains.join(', ') : 'General Technical'
    };

    try {
      const response = await fetch(
        `${API_BASE}/api/auth/interview-rooms`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(interviewData),
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success('Interview scheduled and saved successfully!');
        trackEvent('room_created', {
          interview_type: interviewType,
          domains: selectedDomains.length > 0 ? selectedDomains.join(', ') : 'General Technical'
        });
        return true;
      } else {
        toast.error(`Failed to save interview: ${data.error || 'Unknown error'}`);
        return false;
      }
    } catch (error) {
      toast.error(`Network error: ${error.message}`);
      return false;
    }
  };

  const handleDeleteInterview = async (roomId) => {
    const isConfirmed = await showConfirm('Are you sure you want to delete this interview? This cannot be undone.');
    if (!isConfirmed) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/auth/interviews/${roomId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });

      const data = await res.json();
      if (res.ok) {
        toast.success('Interview deleted successfully');
        trackEvent('room_deleted', { room_id: roomId });
        // Optimistically update all open modal lists immediately
        setPendingInterviewsList((prev) => prev.filter((i) => i.room_id !== roomId));
        setTodaysInterviewsList((prev) => prev.filter((i) => i.room_id !== roomId));
        setTotalInterviewsList((prev) => prev.filter((i) => i.room_id !== roomId));
        setPendingInvites((prev) => Math.max(0, prev - 1));
        setTotalInterviews((prev) => Math.max(0, prev - 1));
        setTodayInterviews((prev) => Math.max(0, prev - 1)); // Heuristic update

        // Pull authoritative counts/lists so cards and modals stay in sync.
        fetchStats();
      } else {
        toast.error(`Failed to delete: ${data.error}`);
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Network error while deleting');
    }
  };

  const handleLogout = async () => {
    const confirmed = await showConfirm('Are you sure you want to logout?');
    if (!confirmed) {
      return;
    }

    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (e) {
      // ignore
    } finally {
      trackEvent('logout', { role: user.role || 'HR' });
      localStorage.removeItem('user');
      localStorage.removeItem('scheduledInterviews');
      navigate('/login');
    }
  };

  const handleViewInCalendar = async () => {
    // Already saved during handleDateSelect, just navigate

    try {
      const response = await fetch(
        `${API_BASE}/api/auth/interview-rooms/user/${userId}`
      );
      if (response.ok) {
        const interviews = await response.json();
        localStorage.setItem(
          'scheduledInterviews',
          JSON.stringify(interviews)
        );
        setSavedToCalendar(true);
        navigate('/calendar-view');
      } else {
        toast.error('Interview room data not found. Redirecting to calendar.');
        navigate('/calendar-view');
      }
    } catch (error) {
      console.error('Error fetching interview rooms:', error);
      toast.error('Network error fetching interviews. Redirecting to calendar.');
      navigate('/calendar-view');
    }
  };
  const renderWithLayout = (content) => (
    <>
      {!hideNavbar && <Navbar isLoggedIn userName={userName} onSchedule={handleCreateNewRoom} onHome={() => setCurrentStep('main')} activeTab="schedule" />}
      {content}
      {!hideNavbar && <Footer />}
    </>
  );
  const handleBackToMain = () => {
    setCurrentStep('main');
    setGeneratedRoomId('');
    setCandidateLink('');
    setInterviewerLink('');
    setSavedToCalendar(false);
    setCandidateName('');
    setCandidateEmail('');
    setSelectedResume(null);
    setPreDefinedQuestions([]);
    setNewQuestionInput('');
    setShowDatePicker(false);
    setShowTimePicker(false);
    setTimePickerMode('hour');
    setShowQuestionsModal(false);
    setIsScheduling(false);
    setFormErrors({ candidateName: '', candidateEmail: '' });
    fetchStats(); // Auto-refresh dashboard stats
  };

  const handleBackToCalendar = () => {
    setCurrentStep('calendar');
  };

  const overviewMap = {
    today: {
      title: "Today's interviews",
      subtitle: 'All interviews scheduled for today.',
      list: todaysInterviewsList,
      empty: "No interviews scheduled for today yet.",
    },
    pending: {
      title: 'Pending invites',
      subtitle: 'Scheduled interviews waiting to be conducted.',
      list: pendingInterviewsList,
      empty: 'No pending invites right now.',
    },
    total: {
      title: 'Total created interviews',
      subtitle: 'Archive of all interviews created by this HR account.',
      list: totalInterviewsList,
      empty: 'No interviews have been created yet.',
    },
  };

  const selectedOverview = overviewMap[activeOverview] || overviewMap.today;

  const upcomingInterviews = React.useMemo(() => {
    const now = new Date();

    return (totalInterviewsList || [])
      .filter((item) => {
        const status = String(item.status || '').toUpperCase();
        if (status === 'COMPLETED' || status === 'CANCELLED') {
          return false;
        }

        if (!item.scheduled_date) {
          return true;
        }

        return parseUTC(item.scheduled_date).getTime() >= now.getTime();
      })
      .sort((a, b) => {
        const aTime = a.scheduled_date ? parseUTC(a.scheduled_date).getTime() : Number.MAX_SAFE_INTEGER;
        const bTime = b.scheduled_date ? parseUTC(b.scheduled_date).getTime() : Number.MAX_SAFE_INTEGER;
        return aTime - bTime;
      });
  }, [totalInterviewsList]);

  const previewUpcomingInterviews = showAllUpcoming
    ? upcomingInterviews
    : upcomingInterviews.slice(0, 2);

  const canExpandUpcoming = upcomingInterviews.length > 2;

  const openOverview = (type) => {
    setActiveOverview(type);
    setShowOverviewModal(true);
  };

  const closeOverview = () => {
    setShowOverviewModal(false);
  };

  // final auth guard (after hooks)
  if (!isValidUser) {
    toast.error('User not authenticated or user data missing. Please login again.');
    navigate('/login');
    return null;
  }


  if (currentStep === 'links') {
    // Composed Date for display
    const finalDate = new Date(selectedDate);
    if (selectedTime) {
      const [hours, minutes] = selectedTime.split(':');
      finalDate.setHours(parseInt(hours, 10));
      finalDate.setMinutes(parseInt(minutes, 10));
    }

    return (






















      <div style={styles.linksPageWrapper}>








        <Toaster position="top-right" />

        {!hideNavbar && <Navbar isLoggedIn userName={userName} onSchedule={handleCreateNewRoom} onHome={() => setCurrentStep('main')} activeTab="schedule" />}























        <div style={{ ...styles.modernContainer, maxWidth: '1100px' }}>
          <style>{`
            .saas-btn-primary:hover {
              transform: scale(1.02);
              box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
            }
            .saas-btn-secondary:hover {
              background: rgba(0,0,0,0.03);
            }
            .saas-link-card {
              transition: all 0.2s ease;
            }
            .saas-link-card:hover {
              transform: translateY(-2px);
              box-shadow: 0 8px 24px rgba(0,0,0,0.06);
              border-color: rgba(255,255,255,0.8);
            }
            .saas-copy-btn:hover {
              transform: scale(1.05);
            }
            /* Animations */
            @keyframes fadeInUp {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes scaleFadeIn {
              0% { opacity: 0; transform: scale(0.5); }
              70% { transform: scale(1.1); }
              100% { opacity: 1; transform: scale(1); }
            }
            @keyframes pulseGlow {
              0% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.4); }
              70% { box-shadow: 0 0 0 15px rgba(22, 163, 74, 0); }
              100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0); }
            }

            /* Responsive Grid */
            .saas-two-column-grid {
              display: grid;
              grid-template-columns: 1fr 1px 1fr;
              gap: 32px;
              align-items: start;
              margin-top: 32px;
            }
            
            .saas-vertical-divider {
              background: rgba(0,0,0,0.06);
              width: 1px;
              height: 100%;
            }

            @media (max-width: 850px) {
              .saas-two-column-grid {
                grid-template-columns: 1fr;
                gap: 40px;
              }
              .saas-vertical-divider {
                display: none;
              }
            }

            /* Mini Cards */
            .saas-mini-card {
              display: flex;
              align-items: center;
              gap: 16px;
              padding: 16px;
              border-radius: 12px;
              background: #f8fafc;
              border: 1px solid rgba(0,0,0,0.03);
              box-shadow: 0 2px 4px rgba(0,0,0,0.02);
              transition: all 0.2s ease;
            }
            .saas-mini-card:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(0,0,0,0.04);
            }

            /* Copy Tooltip */
            .copy-tooltip-container {
              position: relative;
              display: inline-flex;
            }
            .copy-tooltip-text {
              visibility: hidden;
              width: 70px;
              background-color: #1e293b;
              color: #fff;
              text-align: center;
              border-radius: 6px;
              padding: 5px;
              position: absolute;
              z-index: 10;
              bottom: 125%;
              left: 50%;
              transform: translateX(-50%);
              opacity: 0;
              transition: opacity 0.3s;
              font-size: 11px;
              font-weight: 500;
              pointer-events: none;
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            }
            .copy-tooltip-text::after {
              content: "";
              position: absolute;
              top: 100%;
              left: 50%;
              margin-left: -5px;
              border-width: 5px;
              border-style: solid;
              border-color: #1e293b transparent transparent transparent;
            }
            .copy-tooltip-container:hover .copy-tooltip-text {
              visibility: visible;
              opacity: 1;
            }
          `}</style>

          <div style={{ ...styles.successBanner, marginBottom: '24px' }}>
            <div style={{
              ...styles.successIconWrapper,
              width: '64px', height: '64px', borderRadius: '32px',
              animation: 'scaleFadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards, pulseGlow 2s infinite'
            }}>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="36" height="36">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 style={{ ...styles.successTitle, fontSize: '28px' }}>You're all set! 🎉 Interview Scheduled Successfully</h2>
            <p style={{ ...styles.successSubtitle, fontSize: '16px' }}>Your interview has been created and is ready to share</p>
          </div>

          <div className="saas-two-column-grid">
            {/* Left Column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <h3 style={{ ...styles.sectionTitle, fontSize: '18px', paddingBottom: '8px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                📊 Interview Details
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className="saas-mini-card">
                  <div style={styles.infoIconWrapper}>
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div style={styles.infoContent}>
                    <span style={styles.infoLabel}>Date & Time</span>
                    <span style={{ ...styles.infoValue, fontSize: '16px' }}>
                      {finalDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} at {finalDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>

                <div className="saas-mini-card">
                  <div style={styles.infoIconWrapper}>
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                  <div style={styles.infoContent}>
                    <span style={styles.infoLabel}>Meeting ID</span>
                    <span style={{ ...styles.meetingIdBadge, background: '#eef2ff', borderRadius: '100px', fontSize: '15px' }}>{generatedRoomId}</span>
                  </div>
                </div>

                {candidateName && (
                  <div className="saas-mini-card">
                    <div style={styles.infoIconWrapper}>
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div style={styles.infoContent}>
                      <span style={styles.infoLabel}>Candidate</span>
                      <span style={{ ...styles.infoValue, fontSize: '16px' }}>{candidateName}</span>
                    </div>
                  </div>
                )}

                {candidateEmail && (
                  <div className="saas-mini-card">
                    <div style={styles.infoIconWrapper}>
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div style={styles.infoContent}>
                      <span style={styles.infoLabel}>Email</span>
                      <span style={{ ...styles.infoValue, fontSize: '16px' }}>{candidateEmail}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="saas-vertical-divider"></div>

            {/* Right Column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <h3 style={{ ...styles.sectionTitle, fontSize: '18px', paddingBottom: '8px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                🔗 Invitation Links
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                <div style={{ ...styles.linkCard, borderRadius: '16px', background: '#f8fafc', padding: '24px' }} className="saas-link-card">
                  <div style={styles.linkCardHeader}>
                    <div style={styles.avatarCircle}>C</div>
                    <div>
                      <h4 style={styles.linkCardTitle}>Candidate Link</h4>
                      <p style={styles.linkCardSubtitle}>Share this with the interview candidate</p>
                    </div>
                  </div>

                  <div style={{ ...styles.linkInputWrapper, marginTop: '8px' }}>
                    <input
                      type="text"
                      value={candidateLink}
                      readOnly
                      onClick={(e) => e.target.select()}
                      style={{ ...styles.modernInput, borderRadius: '12px' }}
                    />
                    <button
                      onClick={copyCandidateLink}
                      title={candidateLinkCopied ? "Copied!" : "Copy to clipboard"}
                      style={{ ...styles.copyButton, borderRadius: '8px', ...(candidateLinkCopied ? styles.copyButtonCopied : {}) }}
                      className="saas-copy-btn"
                    >
                      {candidateLinkCopied ? (
                        <><svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 13l4 4L19 7" /></svg>Link Copied!</>
                      ) : (
                        <><svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg> Copy</>
                      )}
                    </button>
                  </div>
                </div>

                {interviewType !== 'AI' && (
                  <div style={{ ...styles.linkCard, borderRadius: '16px', background: '#f8fafc', padding: '24px' }} className="saas-link-card">
                    <div style={styles.linkCardHeader}>
                      <div style={{ ...styles.avatarCircle, background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)' }}>I</div>
                      <div>
                        <h4 style={styles.linkCardTitle}>Interviewer Link</h4>
                        <p style={styles.linkCardSubtitle}>Share with HR team and interviewers</p>
                      </div>
                    </div>

                    <div style={{ ...styles.linkInputWrapper, marginTop: '8px' }}>
                      <input
                        type="text"
                        value={interviewerLink || ''}
                        readOnly
                        onClick={(e) => e.target.select()}
                        style={{ ...styles.modernInput, borderRadius: '12px' }}
                      />
                      <button
                        onClick={copyInterviewerLink}
                        title={interviewerLinkCopied ? "Copied!" : "Copy to clipboard"}
                        style={{ ...styles.copyButton, borderRadius: '8px', ...(interviewerLinkCopied ? styles.copyButtonCopied : {}) }}
                        className="saas-copy-btn"
                      >
                        {interviewerLinkCopied ? (
                          <><svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 13l4 4L19 7" /></svg> Link Copied!</>
                        ) : (
                          <><svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg> Copy</>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '40px', borderTop: '1px solid rgba(0,0,0,0.05)', paddingTop: '32px' }}>
            <button onClick={handleViewInCalendar} style={{ ...styles.primaryBtnSaaS, borderRadius: '100px' }} className="saas-btn-primary">
              {savedToCalendar ? '✓ Saved to Calendar' : 'View in Calendar'}
            </button>
            <button onClick={handleBackToMain} style={{ ...styles.secondaryBtnSaaS, borderRadius: '100px' }} className="saas-btn-secondary">
              Back to Dashboard
            </button>
          </div>
        </div>

        {!hideNavbar && <Footer />}
      </div>
    );
  }

  // STEP 2: Calendar Selection
  if (currentStep === 'calendar') {
    const formattedSelectedDate = selectedDate.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

    const selectedDateTime = new Date(selectedDate);
    if (selectedTime) {
      const [hours, minutes] = selectedTime.split(':');
      selectedDateTime.setHours(parseInt(hours, 10));
      selectedDateTime.setMinutes(parseInt(minutes, 10));
    }

    const scheduleSummary = selectedDateTime.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    const timeParts = to12HourParts(selectedTime);
    const displayTime = `${timeParts.hour}:${timeParts.minute} ${timeParts.period}`;
    const hourOptions = ['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'];
    const minuteOptions = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];
    const clockValues = timePickerMode === 'hour' ? hourOptions : minuteOptions;

    const setTimePart = (part, value) => {
      const next = { ...timeParts, [part]: value };
      setSelectedTime(to24Hour(next.hour, next.minute, next.period));

      if (part === 'hour') {
        setTimePickerMode('minute');
      }
    };

    const getClockButtonStyle = (index, total = 12) => {
      const angle = (index / total) * (Math.PI * 2) - Math.PI / 2;
      const radius = 96;
      return {
        left: `calc(50% + ${Math.cos(angle) * radius}px)`,
        top: `calc(50% + ${Math.sin(angle) * radius}px)`,
      };
    };

    const handlePointerInteraction = (e) => {
      if (!clockRef.current) return;
      const rect = clockRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const dy = e.clientY - centerY;
      const dx = e.clientX - centerX;
      let angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
      if (angle < 0) angle += 360;
      
      const step = 360 / clockValues.length;
      let index = Math.round(angle / step);
      if (index >= clockValues.length) index = 0;
      
      const newValue = clockValues[index];
      const activeVal = timePickerMode === 'hour' ? timeParts.hour : timeParts.minute;
      
      if (newValue !== activeVal) {
        const next = { ...timeParts, [timePickerMode]: newValue };
        setSelectedTime(to24Hour(next.hour, next.minute, next.period));
      }
    };

    const onPointerDown = (e) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      isDraggingClock.current = true;
      handlePointerInteraction(e);
    };

    const onPointerMove = (e) => {
      if (!isDraggingClock.current) return;
      handlePointerInteraction(e);
    };

    const onPointerUp = (e) => {
      if (isDraggingClock.current) {
        e.currentTarget.releasePointerCapture(e.pointerId);
        isDraggingClock.current = false;
        if (timePickerMode === 'hour') setTimePickerMode('minute');
      }
    };

    const handleDocxUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (!file.name.endsWith('.docx')) {
        toast.error('Please upload a valid .docx file.');
        return;
      }

      setIsParsingDocx(true);
      const minDelay = new Promise((r) => setTimeout(r, 2500));

      try {
        const arrayBuffer = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target.result);
          reader.onerror = () => reject(new Error('Error reading the file.'));
          reader.readAsArrayBuffer(file);
        });

        const [result] = await Promise.all([
          mammoth.extractRawText({ arrayBuffer }),
          minDelay,
        ]);
        const text = result.value;

        if (text) {
          const cleanedText = text.replace(/\n\s*\n/g, '\n').trim();
          setNewQuestionInput((prev) => (prev ? `${prev}\n${cleanedText}` : cleanedText));
          toast.success('Docx parsed successfully. You can edit the questions below.');
        } else {
          toast.error('Could not extract text from the document.');
        }
      } catch (error) {
        await minDelay;
        toast.error(error.message || 'Error processing document.');
      } finally {
        setIsParsingDocx(false);
      }
      e.target.value = '';
    };

    const addQuestions = () => {
      if (!newQuestionInput.trim()) {
        toast.error('Please enter at least one question.');
        return;
      }

      const questions = newQuestionInput
        .split('\n')
        .map((q) => q.trim())
        .filter((q) => q.length > 0);

      if (questions.length === 0) {
        toast.error('Please enter at least one valid question.');
        return;
      }

      setPreDefinedQuestions((prev) => [...prev, ...questions]);
      setNewQuestionInput('');
      setShowQuestionsModal(false);
      toast.success(`Added ${questions.length} question${questions.length > 1 ? 's' : ''}`);
    };

    const addAiSuggestedQuestions = () => {
      const suggestions = [
        'Tell us about a challenging project you worked on recently.',
        'How do you prioritize tasks when deadlines overlap?',
        'Describe a situation where you resolved a team conflict.',
      ];
      setPreDefinedQuestions((prev) => [...prev, ...suggestions]);
      toast.success('AI suggestions added.');
    };

    return renderWithLayout(
      <div className="schedule-page-shell">
        <Toaster position="top-right" />

        <div className="schedule-card">
          <div className="schedule-header">
            <button className="schedule-back-btn" onClick={handleBackToMain}>
              <span aria-hidden="true">←</span>
              Back
            </button>

            <div className="schedule-header-center">
              <h2>Schedule Interview</h2>
              <p>Set up candidate interview details</p>
            </div>

            <span className="schedule-header-spacer" aria-hidden="true" />
          </div>

          <div className="schedule-divider" />

          <div className="schedule-form-wrap">
            <div
              className={`schedule-field ${formErrors.candidateName ? 'is-error' : ''} ${candidateName.trim() ? 'is-valid' : ''}`}
            >
              <span className="schedule-field-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
                  <path d="M4 20a8 8 0 0116 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              <input
                type="text"
                placeholder=" "
                value={candidateName}
                onChange={(e) => {
                  setCandidateName(e.target.value);
                  if (formErrors.candidateName) {
                    setFormErrors((prev) => ({ ...prev, candidateName: '' }));
                  }
                }}
                onBlur={() => {
                  if (!candidateName.trim()) {
                    setFormErrors((prev) => ({ ...prev, candidateName: 'Candidate name is required' }));
                  }
                }}
              />
              <label>Candidate Full Name</label>
            </div>
            {formErrors.candidateName && <p className="schedule-field-error">{formErrors.candidateName}</p>}

            <div
              className={`schedule-field ${formErrors.candidateEmail ? 'is-error' : ''} ${candidateEmail.trim() && isValidEmail(candidateEmail) ? 'is-valid' : ''}`}
            >
              <span className="schedule-field-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              <input
                type="email"
                placeholder=" "
                value={candidateEmail}
                onChange={(e) => {
                  setCandidateEmail(e.target.value);
                  if (formErrors.candidateEmail) {
                    setFormErrors((prev) => ({ ...prev, candidateEmail: '' }));
                  }
                }}
                onBlur={() => {
                  if (!candidateEmail.trim()) {
                    setFormErrors((prev) => ({ ...prev, candidateEmail: 'Candidate email is required' }));
                  } else if (!isValidEmail(candidateEmail)) {
                    setFormErrors((prev) => ({ ...prev, candidateEmail: 'Please enter a valid email address' }));
                  }
                }}
              />
              <label>Candidate Email Address</label>
            </div>
            {formErrors.candidateEmail && <p className="schedule-field-error">{formErrors.candidateEmail}</p>}

            <div className="schedule-datetime-row">
              <div className="schedule-picker-group" ref={datePickerRef}>
                <label className="schedule-group-label">Date</label>
                <button
                  type="button"
                  className="schedule-picker-btn"
                  onClick={() => {
                    setShowTimePicker(false);
                    setShowDatePicker((prev) => !prev);
                  }}
                >
                  <span className="schedule-picker-value">{formattedSelectedDate}</span>
                  <span className="schedule-picker-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="5" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="2" />
                      <path d="M8 3v4M16 3v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                </button>

                {showDatePicker && (
                  <div className="schedule-calendar-popover">
                    <Calendar
                      onChange={(date) => {
                        setSelectedDate(date);
                        setShowDatePicker(false);
                      }}
                      value={selectedDate}
                      minDate={new Date()}
                      className="view-calendar schedule-calendar"
                      calendarType="gregory"
                    />
                  </div>
                )}
              </div>

              <div className="schedule-picker-group" ref={timePickerRef}>
                <label className="schedule-group-label">Time</label>
                <button
                  type="button"
                  className="schedule-picker-btn"
                  onClick={() => {
                    setShowDatePicker(false);
                    setTimePickerMode('hour');
                    setShowTimePicker((prev) => !prev);
                  }}
                >
                  <span className="schedule-picker-value">{displayTime}</span>
                  <span className="schedule-picker-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
                      <path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                </button>

                {showTimePicker && (
                  <div className="schedule-time-popover">
                    <div className="schedule-time-head">
                      <button
                        type="button"
                        className={`schedule-time-head-btn ${timePickerMode === 'hour' ? 'active' : ''}`}
                        onClick={() => setTimePickerMode('hour')}
                      >
                        {timeParts.hour}
                      </button>
                      <span>:</span>
                      <button
                        type="button"
                        className={`schedule-time-head-btn ${timePickerMode === 'minute' ? 'active' : ''}`}
                        onClick={() => setTimePickerMode('minute')}
                      >
                        {timeParts.minute}
                      </button>
                      <div className="schedule-time-period-switch">
                        {['AM', 'PM'].map((period) => (
                          <button
                            key={period}
                            type="button"
                            className={timeParts.period === period ? 'active' : ''}
                            onClick={() => setTimePart('period', period)}
                          >
                            {period}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div 
                      className="schedule-time-clock" 
                      role="group" 
                      aria-label="Clock time picker"
                      ref={clockRef}
                      onPointerDown={onPointerDown}
                      onPointerMove={onPointerMove}
                      onPointerUp={onPointerUp}
                      onPointerCancel={onPointerUp}
                    >
                      {clockValues.map((value, index) => {
                        const isActive = timePickerMode === 'hour'
                          ? timeParts.hour === value
                          : timeParts.minute === value;

                        return (
                          <button
                            key={value}
                            type="button"
                            className={`schedule-time-clock-dot ${isActive ? 'active' : ''}`}
                            style={getClockButtonStyle(index, clockValues.length)}
                            onClick={(e) => {
                                e.stopPropagation();
                                setTimePart(timePickerMode, value);
                            }}
                          >
                            {value}
                          </button>
                        );
                      })}
                      {(() => {
                        const activeVal = timePickerMode === 'hour' ? timeParts.hour : timeParts.minute;
                        const activeIndex = clockValues.indexOf(activeVal);
                        const rotation = activeIndex >= 0 ? activeIndex * (360 / clockValues.length) : 0;
                        return (
                          <div 
                            className={`schedule-time-clock-hand ${timePickerMode === 'minute' ? 'is-minute' : ''}`}
                            style={{ transform: `rotate(${rotation}deg)` }} 
                            aria-hidden="true" 
                          />
                        );
                      })()}
                      <div className="schedule-time-clock-center" aria-hidden="true" />
                    </div>

                    <div className="schedule-time-footer">
                      <button type="button" onClick={() => setShowTimePicker(false)}>
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="schedule-mode-block">
              <label className="schedule-group-label">Interview Mode</label>
              <div className={`schedule-segmented-control mode-${interviewType.toLowerCase()}`} role="tablist" aria-label="Interview mode">
                <div className="schedule-slider" aria-hidden="true" />
                <button
                  type="button"
                  className={`schedule-segment ${interviewType === 'AI' ? 'active' : ''}`}
                  onClick={() => setInterviewType('AI')}
                >
                  AI
                </button>
                <button
                  type="button"
                  className={`schedule-segment ${interviewType === 'PERSONAL' ? 'active' : ''}`}
                  onClick={() => setInterviewType('PERSONAL')}
                >
                  Interviewer
                </button>
              </div>
            </div>

            {interviewType === 'AI' && (
              <div className="schedule-domain-section" style={{marginBottom: '24px'}}>
                <label className="schedule-group-label" style={{marginBottom: '8px', display: 'block'}}>Candidate Resume (PDF/DOCX) <span style={{color: '#64748b', fontSize: '13px', fontWeight: 'normal'}}>(Optional)</span></label>
                <div style={{
                    border: `2px dashed ${formErrors.resume ? '#ef4444' : (selectedResume ? '#6366f1' : '#cbd5e1')}`,
                    borderRadius: '12px',
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: selectedResume ? '#eef2ff' : '#f8fafc',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                }}>
                    <input
                        type="file"
                        accept=".pdf,.docx"
                        id="resume-upload"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                                setSelectedResume(e.target.files[0]);
                                setFormErrors(prev => ({...prev, resume: ''}));
                            }
                        }}
                    />
                    <label htmlFor="resume-upload" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '100%', height: '100%' }}>
                        <span style={{ fontSize: '24px' }}>{selectedResume ? '✅' : '📄'}</span>
                        <span style={{ color: selectedResume ? '#4338ca' : '#64748b', fontWeight: 500, fontSize: '14px' }}>
                            {selectedResume ? selectedResume.name : 'Click to select resume PDF or DOCX'}
                        </span>
                        {selectedResume && (
                            <span style={{ color: '#64748b', fontSize: '12px' }}>
                                {(selectedResume.size / 1024).toFixed(1)} KB
                            </span>
                        )}
                    </label>
                </div>
                {formErrors.resume && <p className="schedule-field-error" style={{marginTop: '8px'}}>{formErrors.resume}</p>}
              </div>
            )}

            {interviewType === 'AI' && (
              <div className="schedule-domain-section">
                <label className="schedule-group-label" style={{marginBottom: '8px', display: 'block'}}>Technology Focus</label>
                
                <div className="schedule-domain-tags">
                  {selectedDomains.map((domain, index) => (
                    <span key={index} className="schedule-domain-tag">
                      {domain}
                      <button type="button" onClick={() => {
                          setSelectedDomains(prev => prev.filter(d => d !== domain));
                      }}>✕</button>
                    </span>
                  ))}
                </div>

                <div className="schedule-domain-controls">
                  <select 
                    className="schedule-domain-dropdown"
                    value=""
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'Other') {
                        setShowOtherDomainInput(true);
                      } else if (val && !selectedDomains.includes(val)) {
                        setSelectedDomains([...selectedDomains, val]);
                      }
                    }}
                  >
                    <option value="" disabled>Add a tech domain...</option>
                    {domainOptions.filter(opt => !selectedDomains.includes(opt)).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                    <option value="Other">Other (Type custom...)</option>
                  </select>

                  {showOtherDomainInput && (
                    <div className="schedule-domain-other">
                      <input 
                        type="text" 
                        placeholder="E.g. Rust, Vue.js..." 
                        value={customDomainInput}
                        onChange={(e) => setCustomDomainInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = customDomainInput.trim();
                            if (val && !selectedDomains.includes(val)) {
                              setSelectedDomains([...selectedDomains, val]);
                            }
                            setCustomDomainInput('');
                            setShowOtherDomainInput(false);
                          }
                        }}
                      />
                      <button 
                        type="button" 
                        className="schedule-domain-add-btn"
                        onClick={() => {
                          const val = customDomainInput.trim();
                          if (val && !selectedDomains.includes(val)) {
                            setSelectedDomains([...selectedDomains, val]);
                          }
                          setCustomDomainInput('');
                          setShowOtherDomainInput(false);
                        }}
                      >
                        Add
                      </button>
                      <button 
                        type="button" 
                        className="schedule-domain-cancel-btn"
                        onClick={() => {
                          setCustomDomainInput('');
                          setShowOtherDomainInput(false);
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {interviewType === 'AI' && (
              <div className="schedule-questions-card">
                <div className="schedule-questions-head">
                  <div>
                    <h4>Interview Questions</h4>
                    <p>Optional questions for candidate</p>
                  </div>
                  <div className="schedule-question-actions">
                    {/* <button
                      type="button"
                      className="schedule-ghost-btn"
                      onClick={addAiSuggestedQuestions}
                    >
                      AI Suggest
                    </button> */}
                    <button
                      type="button"
                      className="schedule-add-btn"
                      onClick={() => setShowQuestionsModal(true)}
                    >
                      <span aria-hidden="true">＋</span>
                      Add Questions
                    </button>
                  </div>
                </div>

                <div className={`schedule-questions-list ${preDefinedQuestions.length === 0 ? 'is-empty' : ''}`}>
                  {preDefinedQuestions.length === 0 ? (
                    <div className="schedule-empty-state">
                      <span className="schedule-empty-icon" aria-hidden="true">🗒</span>
                      <p>No questions added yet</p>
                    </div>
                  ) : (
                    preDefinedQuestions.map((q, idx) => (
                      <div className="schedule-question-item" key={`${q}-${idx}`}>
                        <span>{q}</span>
                        <button
                          type="button"
                          onClick={() => setPreDefinedQuestions(preDefinedQuestions.filter((_, i) => i !== idx))}
                          title="Remove question"
                        >
                          ✕
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="schedule-sticky-footer">
            <p>Scheduled for {scheduleSummary.replace(',', ' •')}</p>
            <button
              type="button"
              className="schedule-submit-btn"
              onClick={handleScheduleInterview}
              disabled={isScheduling}
            >
              {isScheduling ? 'Scheduling...' : 'Schedule'}
            </button>
          </div>

          {showQuestionsModal && (
            <div className="schedule-modal-overlay" onClick={() => setShowQuestionsModal(false)}>
              <div className="schedule-modal-panel" onClick={(e) => e.stopPropagation()}>
                <div className="schedule-modal-head">
                  <h4>Add Interview Questions</h4>
                  <button
                    type="button"
                    onClick={() => setShowQuestionsModal(false)}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>

                <div className="schedule-modal-upload-action">
                  <label className={`schedule-docx-upload-btn ${isParsingDocx ? 'is-loading' : ''}`} title="Upload Docx file to extract questions">
                    <span aria-hidden="true" className="schedule-upload-icon">📄</span>
                    {isParsingDocx ? 'Parsing Docx...' : 'Upload .docx Document'}
                    <input
                      type="file"
                      accept=".docx"
                      onChange={handleDocxUpload}
                      disabled={isParsingDocx}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <span className="schedule-upload-hint">Upload a Word document to populate questions automatically.</span>
                </div>

                <div className="schedule-modal-textarea-wrap">
                  {isParsingDocx && (
                    <div className="schedule-docx-loader-overlay">
                      <div className="schedule-docx-spinner" />
                      <span>Extracting questions from document…</span>
                    </div>
                  )}
                  <textarea
                    value={newQuestionInput}
                    onChange={(e) => setNewQuestionInput(e.target.value)}
                    placeholder="Type or paste each question on a new line..."
                    className="schedule-modal-textarea"
                    disabled={isParsingDocx}
                  />
                </div>

                <div className="schedule-modal-footer">
                  <button
                    type="button"
                    className="schedule-modal-cancel"
                    onClick={() => setShowQuestionsModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="schedule-modal-primary"
                    onClick={addQuestions}
                  >
                    Add to List
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={styles.pageWrapper}>
        <Toaster position="top-right" />
        <div style={styles.dashboardShell}>
          {/* Left rail */}


          {/* Main */}
          <main style={styles.dashboardMain}>
            {!hideNavbar && <Navbar isLoggedIn userName={userName} onSchedule={handleCreateNewRoom} onHome={() => setCurrentStep('main')} activeTab="dashboard" />}

            {/* Stats */}
            <section style={styles.analyticsStatsGrid}>
              <div
                onClick={() => openOverview('today')}
                style={{ ...styles.analyticsStatCard, cursor: 'pointer' }}
              >
                <div style={styles.analyticsStatHead}>
                  <span style={styles.analyticsStatLabel}>Today interviews</span>
                  <span style={{ ...styles.analyticsPill, background: '#eef2ff', color: '#4338ca' }}>
                    Live
                  </span>
                </div>
                <div style={styles.analyticsStatValue}>
                  {statsLoading ? '…' : todayInterviews}
                </div>
                <div style={styles.analyticsStatHint}>Open today's interview slots</div>
              </div>

              <div
                onClick={() => openOverview('pending')}
                style={{ ...styles.analyticsStatCard, cursor: 'pointer' }}
              >
                <div style={styles.analyticsStatHead}>
                  <span style={styles.analyticsStatLabel}>Pending invites</span>
                  <span style={{ ...styles.analyticsPill, background: '#fff7ed', color: '#c2410c' }}>
                    Action
                  </span>
                </div>
                <div style={styles.analyticsStatValue}>
                  {statsLoading ? '…' : pendingInvites}
                </div>
                <div style={styles.analyticsStatHint}>Manage or delete pending rooms</div>
              </div>

              <div
                onClick={() => openOverview('total')}
                style={{ ...styles.analyticsStatCard, cursor: 'pointer' }}
              >
                <div style={styles.analyticsStatHead}>
                  <span style={styles.analyticsStatLabel}>Total created</span>
                  <span style={{ ...styles.analyticsPill, background: '#ecfdf5', color: '#047857' }}>
                    Archive
                  </span>
                </div>
                <div style={styles.analyticsStatValue}>
                  {statsLoading ? '…' : totalInterviews}
                </div>
                <div style={styles.analyticsStatHint}>View all interviews and results</div>
              </div>
            </section>

            {/* Content area */}
            <section style={styles.analyticsContentGrid}>
              {/* Left main panel */}
              <div style={styles.analyticsMainColumn}>
                <div style={{ ...styles.panelCard, padding: '14px' }}>
                  <div style={styles.panelHeader}>
                    <div>
                      <h3 style={styles.panelTitle}>Upcoming interview actions</h3>
                      <p style={styles.panelSubtitle}>
                        Next scheduled interviews for your review.
                      </p>
                    </div>
                    <span style={styles.panelCountBadge}>Upcoming {upcomingInterviews.length}</span>
                  </div>

                  {statsLoading ? (
                    <div style={styles.emptyOverview}>Loading upcoming interviews...</div>
                  ) : previewUpcomingInterviews.length === 0 ? (
                    <div style={styles.emptyOverview}>No upcoming interviews. Create one to get started.</div>
                  ) : (
                    <>
                      <div
                        style={{
                          ...styles.upcomingListWrap,
                          ...(showAllUpcoming ? styles.upcomingListWrapExpanded : {}),
                        }}
                      >
                        {previewUpcomingInterviews.map((item) => {
                          const status = String(item.status || 'SCHEDULED').toUpperCase();
                          const statusTone = status === 'SCHEDULED'
                            ? { bg: '#eef2ff', fg: '#4338ca', border: '#c7d2fe' }
                            : { bg: '#fff7ed', fg: '#9a3412', border: '#fdba74' };
                          const candidateJoinLink = item.candidate_link || generateCandidateLink(item.room_id);
                          const interviewerJoinLink = item.interviewer_link || (item.interview_type === 'AI' ? '' : generateInterviewerLink(item.room_id));
                          const scheduledDate = item.scheduled_date
                            ? parseUTC(item.scheduled_date).toLocaleString([], {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                            : 'Schedule pending';

                          return (
                            <div key={item.room_id} style={styles.upcomingItem}>
                              <span style={styles.upcomingStrip} aria-hidden="true" />
                              <div style={styles.upcomingItemTop}>
                                <div style={styles.upcomingIdentity}>
                                  <div style={styles.upcomingCandidate}>{item.candidate_name || 'Candidate not set'}</div>
                                  <div style={styles.upcomingMeta}>Room: {item.room_id}</div>
                                </div>
                                <div style={styles.upcomingTopActions}>
                                  <span
                                    style={{
                                      ...styles.upcomingStatus,
                                      background: statusTone.bg,
                                      color: statusTone.fg,
                                      borderColor: statusTone.border,
                                    }}
                                  >
                                    {status}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteInterview(item.room_id)}
                                    style={styles.upcomingDeleteBtnCompact}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>

                              <div style={styles.upcomingScheduleRow}>
                                <span style={styles.upcomingScheduleLabel}>{status}</span>
                                <span style={styles.upcomingScheduleValue}>{scheduledDate}</span>
                              </div>

                              <div style={styles.upcomingLinksRow}>
                                {candidateJoinLink && (
                                  <div style={{ ...styles.upcomingPillLink, ...styles.candidatePill, padding: '4px 6px 4px 16px', gap: '8px' }}>
                                    <a
                                      href={candidateJoinLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M23 7l-7 5 7 5V7z" />
                                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                      </svg>
                                      Candidate Link
                                    </a>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.preventDefault(); handleCopyLink(candidateJoinLink, 'Candidate link', `upcoming_cand_${item.room_id}`); }}
                                      style={{ ...styles.copyIconButtonCandidate, position: 'relative' }}
                                      title="Copy link"
                                    >
                                      {copiedLinkId === `upcoming_cand_${item.room_id}` ? (
                                        <>
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                          <span style={{ position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)', background: '#333', color: '#fff', fontSize: '11px', padding: '4px 8px', borderRadius: '4px', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 10 }}>Link Copied!</span>
                                          <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', borderTop: '4px solid #333', borderRight: '4px solid transparent', borderLeft: '4px solid transparent', pointerEvents: 'none', zIndex: 10 }}></div>
                                        </>
                                      ) : (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                                        </svg>
                                      )}
                                    </button>
                                  </div>
                                )}
                                {interviewerJoinLink && (
                                  <div style={{ ...styles.upcomingPillLink, ...styles.interviewerPill, padding: '4px 6px 4px 16px', gap: '8px' }}>
                                    <a
                                      href={interviewerJoinLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M23 7l-7 5 7 5V7z" />
                                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                      </svg>
                                      Interviewer Link
                                    </a>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.preventDefault(); handleCopyLink(interviewerJoinLink, 'Interviewer link', `upcoming_int_${item.room_id}`); }}
                                      style={{ ...styles.copyIconButtonInterviewer, position: 'relative' }}
                                      title="Copy link"
                                    >
                                      {copiedLinkId === `upcoming_int_${item.room_id}` ? (
                                        <>
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                          <span style={{ position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)', background: '#333', color: '#fff', fontSize: '11px', padding: '4px 8px', borderRadius: '4px', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 10 }}>Link Copied!</span>
                                          <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', borderTop: '4px solid #333', borderRight: '4px solid transparent', borderLeft: '4px solid transparent', pointerEvents: 'none', zIndex: 10 }}></div>
                                        </>
                                      ) : (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                                        </svg>
                                      )}
                                    </button>
                                  </div>
                                )}
                              </div>

                            </div>
                          );
                        })}
                      </div>

                      {canExpandUpcoming && (
                        <div style={styles.upcomingFooterActions}>
                          <button
                            type="button"
                            style={styles.upcomingViewMoreBtn}
                            onClick={() => setShowAllUpcoming((prev) => !prev)}
                          >
                            {showAllUpcoming ? 'Show less' : `View more (${upcomingInterviews.length - 2})`}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>


              </div>

              {/* Right insight panel */}
              <div style={styles.analyticsSideColumn}>


                <div style={styles.insightCard}>
                  <div style={styles.panelHeader}>
                    <div>
                      <h3 style={styles.panelTitle}>Recommended flow</h3>
                      <p style={styles.panelSubtitle}>Suggested order for HR operations.</p>
                    </div>
                  </div>

                  <div style={styles.stepList}>
                    <div style={styles.stepItem}>
                      <div style={styles.stepNo}>1</div>
                      <div>
                        <div style={styles.stepTitle}>Create a room</div>
                        <div style={styles.stepText}>Generate candidate and interviewer links.</div>
                      </div>
                    </div>

                    <div style={styles.stepItem}>
                      <div style={styles.stepNo}>2</div>
                      <div>
                        <div style={styles.stepTitle}>Assign candidate</div>
                        <div style={styles.stepText}>Add full name, email, and interview mode.</div>
                      </div>
                    </div>

                    <div style={styles.stepItem}>
                      <div style={styles.stepNo}>3</div>
                      <div>
                        <div style={styles.stepTitle}>Track progress</div>
                        <div style={styles.stepText}>Review today's items and completed results.</div>
                      </div>
                    </div>
                  </div>
                </div>


              </div>
            </section>
          </main>
        </div>

        {showOverviewModal && (
          <div style={styles.overviewModalOverlay} onClick={closeOverview}>
            <div style={styles.overviewModalCard} onClick={(e) => e.stopPropagation()}>
              <div style={styles.overviewModalHead}>
                <div>
                  <h3 style={styles.panelTitle}>{selectedOverview.title}</h3>
                  <p style={styles.panelSubtitle}>{selectedOverview.subtitle}</p>
                </div>
                <button onClick={closeOverview} style={styles.modalCloseBtn}>
                  Close
                </button>
              </div>

              {statsLoading ? (
                <div style={styles.emptyOverview}>Loading overview...</div>
              ) : selectedOverview.list.length === 0 ? (
                <div style={styles.emptyOverview}>{selectedOverview.empty}</div>
              ) : (
                <div style={styles.overviewList}>
                  {selectedOverview.list.map((item) => {
                    const isCompleted = item.status === 'COMPLETED';
                    const statusTone = isCompleted
                      ? { bg: '#dcfce7', fg: '#166534', border: '#86efac' }
                      : { bg: '#fff7ed', fg: '#9a3412', border: '#fdba74' };
                    const candidateJoinLink = item.candidate_link || generateCandidateLink(item.room_id);
                    const interviewerJoinLink = item.interviewer_link || (item.interview_type === 'AI' ? '' : generateInterviewerLink(item.room_id));
                    const scheduledDate = item.scheduled_date
                      ? parseUTC(item.scheduled_date).toLocaleString([], {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                      : 'No schedule';

                    return (
                      <div
                        key={item.room_id}
                        style={{
                          ...styles.overviewItem,
                          borderLeft: `4px solid ${statusTone.border}`,
                        }}
                      >
                        <div style={styles.overviewItemTop}>
                          <div style={styles.overviewIdentity}>
                            <div style={styles.overviewCandidate}>{item.candidate_name || 'Candidate not set'}</div>
                            <div style={styles.overviewMeta}>Room: {item.room_id}</div>
                          </div>
                          <span
                            style={{
                              ...styles.overviewStatus,
                              background: statusTone.bg,
                              color: statusTone.fg,
                              borderColor: statusTone.border,
                            }}
                          >
                            {item.status || 'SCHEDULED'}
                          </span>
                        </div>

                        <div style={styles.overviewScheduleRow}>
                          <span style={styles.overviewScheduleLabel}>Scheduled</span>
                          <span style={styles.overviewScheduleValue}>{scheduledDate}</span>
                        </div>

                        {!isCompleted && activeOverview !== 'pending' && (
                          <div style={styles.overviewLinksRow}>
                            {candidateJoinLink && (
                              <div style={{ ...styles.upcomingPillLink, ...styles.candidatePill, fontSize: '12px', padding: '3px 4px 3px 12px', gap: '6px' }}>
                                <a
                                  href={candidateJoinLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: 'inherit', textDecoration: 'none' }}
                                >
                                  Candidate
                                </a>
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); handleCopyLink(candidateJoinLink, 'Candidate link', `overview_cand_${item.room_id}`); }}
                                  style={{ ...styles.copyIconButtonCandidate, padding: '3px', position: 'relative' }}
                                  title="Copy link"
                                >
                                  {copiedLinkId === `overview_cand_${item.room_id}` ? (
                                    <>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                      <span style={{ position: 'absolute', bottom: 'calc(100% + 5px)', left: '50%', transform: 'translateX(-50%)', background: '#333', color: '#fff', fontSize: '10px', padding: '3px 6px', borderRadius: '4px', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 10 }}> Link Copied!</span>
                                      <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', borderTop: '4px solid #333', borderRight: '4px solid transparent', borderLeft: '4px solid transparent', pointerEvents: 'none', zIndex: 10 }}></div>
                                    </>
                                  ) : (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                                    </svg>
                                  )}
                                </button>
                              </div>
                            )}
                            {interviewerJoinLink && (
                              <div style={{ ...styles.upcomingPillLink, ...styles.interviewerPill, fontSize: '12px', padding: '3px 4px 3px 12px', gap: '6px' }}>
                                <a
                                  href={interviewerJoinLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: 'inherit', textDecoration: 'none' }}
                                >
                                  Interviewer
                                </a>
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); handleCopyLink(interviewerJoinLink, 'Interviewer link', `overview_int_${item.room_id}`); }}
                                  style={{ ...styles.copyIconButtonInterviewer, padding: '3px', position: 'relative' }}
                                  title="Copy link"
                                >
                                  {copiedLinkId === `overview_int_${item.room_id}` ? (
                                    <>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                      <span style={{ position: 'absolute', bottom: 'calc(100% + 5px)', left: '50%', transform: 'translateX(-50%)', background: '#333', color: '#fff', fontSize: '10px', padding: '3px 6px', borderRadius: '4px', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 10 }}> Link Copied!</span>
                                      <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', borderTop: '4px solid #333', borderRight: '4px solid transparent', borderLeft: '4px solid transparent', pointerEvents: 'none', zIndex: 10 }}></div>
                                    </>
                                  ) : (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                                    </svg>
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        <div style={styles.overviewActions}>
                          {isCompleted && (
                            <button
                              onClick={() => navigate(`/results/${item.room_id}`)}
                              style={styles.linkActionBtn}
                            >
                              View result
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteInterview(item.room_id)}
                            style={styles.deleteActionBtn}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
const styles = {
  pageWrapper: {
    height: '100vh',
    background: 'var(--bg-page)',
    padding: '0',
    margin: '0',
    overflow: 'hidden',
    fontFamily: `'Inter', 'Segoe UI', sans-serif`,
  },
  linksPageWrapper: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--bg-page)',
    margin: '0',
    paddingBottom: '0',
    boxSizing: 'border-box',
    fontFamily: `'Inter', 'Poppins', sans-serif`,
  },
  modernContainer: {
    width: '100%',
    maxWidth: '1100px',
    margin: '40px auto',
    background: 'var(--bg-card-glass)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderRadius: '24px',
    padding: '24px 40px',
    border: '1px solid var(--border-card)',
    boxShadow: 'var(--shadow-lg)',
    animation: 'fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards',
  },
  modernHeader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '32px',
    borderBottom: '1px solid var(--divider-color)',
    paddingBottom: '24px',
  },
  modernTitle: {
    fontSize: '28px',
    fontFamily: `'Inter', 'Poppins', sans-serif`,
    fontWeight: '700',
    color: 'var(--text-primary)',
    margin: 0,
    textAlign: 'center',
    letterSpacing: '-0.02em',
  },
  successBanner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '40px',
  },
  successIconWrapper: {
    width: '56px',
    height: '56px',
    borderRadius: '28px',
    background: '#dcfce7',
    color: '#16a34a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.15)',
  },
  successTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    margin: '0 0 8px 0',
    fontFamily: `'Inter', 'Poppins', sans-serif`,
    letterSpacing: '-0.01em',
  },
  successSubtitle: {
    fontSize: '15px',
    color: 'var(--text-muted)',
    margin: 0,
  },
  infoCard: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px',
    marginBottom: '40px',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  infoIconWrapper: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--divider-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6366f1',
    flexShrink: 0,
  },
  infoContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  infoLabel: {
    fontSize: '13px',
    fontWeight: '500',
    color: 'var(--text-muted)',
  },
  infoValue: {
    fontSize: '15px',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  meetingIdBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#e0e7ff',
    color: '#4338ca',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '700',
    letterSpacing: '0.5px',
  },
  divider: {
    height: '1px',
    background: 'var(--divider-color)',
    margin: '32px 0',
  },
  linkSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    marginBottom: '40px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontFamily: `'Inter', 'Poppins', sans-serif`,
    fontWeight: '600',
    color: 'var(--text-secondary)',
    margin: 0,
  },
  linkCard: {
    background: 'var(--bg-card-glass)',
    borderRadius: '16px',
    padding: '20px',
    border: '1px solid var(--border-card)',
    boxShadow: 'var(--shadow-sm)',
    transition: 'all 0.2s ease',
  },
  linkCardHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 15px rgba(0,0,0,0.04)',
  },
  linkCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  avatarCircle: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600',
    fontSize: '16px',
  },
  linkCardTitle: {
    margin: 0,
    fontSize: '15px',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  linkCardSubtitle: {
    margin: '2px 0 0 0',
    fontSize: '13px',
    color: 'var(--text-muted)',
  },
  linkInputWrapper: {
    display: 'flex',
    alignItems: 'center',
    background: 'var(--bg-input)',
    border: '1px solid var(--border-input)',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
    transition: 'border-color 0.2s',
  },
  modernInput: {
    flex: 1,
    padding: '12px 16px',
    fontSize: '14px',
    color: 'var(--text-secondary)',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    fontFamily: `'Inter', monospace`,
  },
  copyButton: {
    padding: '12px 16px',
    background: 'var(--bg-surface)',
    color: '#4f46e5',
    border: 'none',
    borderLeft: '1px solid var(--border-input)',
    fontWeight: '600',
    fontSize: '13px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s',
  },
  copyButtonCopied: {
    background: '#dcfce7',
    color: '#16a34a',
    borderLeftColor: '#bbf7d0',
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  primaryBtnSaaS: {
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '100px',
    padding: '12px 28px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
    transition: 'all 0.2s ease',
  },
  secondaryBtnSaaS: {
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border-color)',
    borderRadius: '100px',
    padding: '12px 28px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  dashboardShell: {
    height: '100vh',
    display: 'grid',
    gridTemplateColumns: '1fr',
    overflow: 'hidden',
  },

  sideRail: {
    background: 'var(--bg-card-glass)',
    border: '1px solid var(--border-color)',
    borderRadius: '28px',
    boxShadow: 'var(--shadow-md)',
    backdropFilter: 'blur(14px)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 14px',
  },

  sideRailTop: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '28px',
    width: '100%',
  },

  sideBrand: {
    display: 'flex',
    justifyContent: 'center',
  },

  sideBrandIcon: {
    width: '52px',
    height: '52px',
    borderRadius: '18px',
    background: 'linear-gradient(135deg, #4f46e5 0%, #2563eb 100%)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: '16px',
    boxShadow: '0 16px 32px rgba(79,70,229,0.28)',
  },

  sideNavList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    width: '100%',
    alignItems: 'center',
  },

  sideNavBtn: {
    width: '48px',
    height: '48px',
    borderRadius: '16px',
    border: '1px solid var(--border-color)',
    background: 'var(--bg-card)',
    color: 'var(--text-tertiary)',
    fontSize: '18px',
    cursor: 'pointer',
    boxShadow: 'var(--shadow-sm)',
  },

  sideNavBtnActive: {
    background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
    color: '#ffffff',
    border: 'none',
  },

  sideLogoutBtn: {
    width: '48px',
    height: '48px',
    borderRadius: '16px',
    border: 'none',
    background: '#fee2e2',
    color: '#dc2626',
    fontSize: '18px',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
  },

  dashboardMain: {
    background: 'var(--bg-card-glass)',
    border: 'none',
    borderRadius: '0',
    boxShadow: 'none',
    backdropFilter: 'blur(18px)',
    padding: '20px 24px',
    height: '100%',
    overflow: 'hidden',
  },

  analyticsTopBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '18px',
    marginBottom: '22px',
    flexWrap: 'wrap',
  },

  analyticsGreeting: {
    fontSize: '34px',
    fontWeight: 800,
    color: 'var(--text-primary)',
    marginBottom: '6px',
  },

  analyticsSubtext: {
    fontSize: '14px',
    color: 'var(--text-muted)',
  },

  analyticsTopRight: {
    display: 'flex',
    margin: '0px 0px 0px 0px',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },

  topGhostBtn: {
    height: '46px',
    padding: '0 18px',
    margin: '0',
    borderRadius: '14px',
    border: '1px solid var(--border-light)',
    background: 'var(--bg-card)',
    color: 'var(--text-secondary)',
    fontWeight: 600,
    cursor: 'pointer',
  },

  topPrimaryBtn: {
    height: '46px',
    paddingRight: '18px',
    paddingLeft: '18px',
    margin: '0',
    borderRadius: '14px',
    border: 'none',
    background: 'linear-gradient(135deg, #4f46e5 0%, #2563eb 100%)',
    color: '#ffffff',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 14px 28px rgba(79,70,229,0.24)',
  },

  topPrimaryBtn1: {
    height: '46px',
    paddingRight: '18px',
    paddingLeft: '18px',
    margin: '0',
    borderRadius: '14px',
    border: 'none',
    background: 'linear-gradient(135deg, #4f46e5 0%, #2563eb 100%)',
    color: '#ffffff',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 14px 28px rgba(79,70,229,0.24)',
  },








  profileChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    background: 'var(--bg-card)',
    borderRadius: '18px',
    border: '1px solid var(--border-color)',
    boxShadow: 'var(--shadow-sm)',
  },

  userNameLabel: {
    fontSize: '11px',
    color: 'var(--text-faint)',
    marginBottom: '3px',
  },

  userNameBig: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 700,
    color: 'var(--text-primary)',
  },

  roleBadgeHR: {
    backgroundColor: '#eef2ff',
    color: '#4338ca',
    fontSize: '10px',
    fontWeight: 800,
    padding: '4px 8px',
    borderRadius: '999px',
  },

  userAvatar: {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #4f46e5 0%, #8b5cf6 100%)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: '16px',
  },

  analyticsStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '16px',
    marginBottom: '20px',
  },

  analyticsStatCard: {
    background: 'var(--bg-stat-card)',
    border: '1px solid var(--border-stat-card)',
    borderRadius: '22px',
    padding: '18px',
    boxShadow: 'var(--shadow-card)',
  },

  analyticsStatHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '14px',
  },

  analyticsStatLabel: {
    fontSize: '13px',
    color: 'var(--text-muted)',
    fontWeight: 600,
  },

  analyticsPill: {
    padding: '6px 10px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 700,
  },

  analyticsStatValue: {
    fontSize: '38px',
    fontWeight: 800,
    lineHeight: 1,
    color: 'var(--text-primary)',
    marginBottom: '10px',
  },

  analyticsStatHint: {
    fontSize: '12px',
    color: 'var(--text-faint)',
  },

  analyticsContentGrid: {
    display: 'grid',
    gridTemplateColumns: '1.55fr 0.85fr',
    gap: '18px',
    alignItems: 'start',
  },

  analyticsMainColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },

  analyticsSideColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },

  heroActionCard: {
    background:
      'linear-gradient(135deg, rgba(79,70,229,0.98) 0%, rgba(37,99,235,0.94) 100%)',
    borderRadius: '28px',
    padding: '28px',
    color: '#ffffff',
    boxShadow: '0 22px 48px rgba(79,70,229,0.26)',
    cursor: 'pointer',
  },

  heroActionBadge: {
    display: 'inline-block',
    padding: '7px 12px',
    borderRadius: '999px',
    background: 'rgba(255,255,255,0.16)',
    border: '1px solid rgba(255,255,255,0.18)',
    fontSize: '11px',
    fontWeight: 700,
    marginBottom: '16px',
  },

  heroActionTitle: {
    fontSize: '30px',
    fontWeight: 800,
    lineHeight: 1.15,
    margin: '0 0 10px 0',
  },

  heroActionText: {
    fontSize: '14px',
    lineHeight: 1.7,
    color: 'rgba(255,255,255,0.88)',
    margin: '0 0 22px 0',
    maxWidth: '620px',
  },

  heroActionFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },

  heroActionButton: {
    height: '46px',
    padding: '0 18px',
    borderRadius: '14px',
    border: 'none',
    background: '#ffffff',
    color: '#1d4ed8',
    fontWeight: 700,
    cursor: 'pointer',
  },

  heroActionMeta: {
    fontSize: '12px',
    color: 'rgba(255,255,255,0.82)',
  },

  panelCard: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderRadius: '24px',
    padding: '22px',
    boxShadow: 'var(--shadow-card)',
  },

  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '18px',
  },

  panelTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--text-primary)',
  },

  panelSubtitle: {
    margin: '6px 0 0 0',
    fontSize: '13px',
    color: 'var(--text-muted)',
  },

  panelCountBadge: {
    alignSelf: 'flex-start',
    background: 'linear-gradient(135deg, rgba(219,234,254,0.95) 0%, rgba(191,219,254,0.95) 100%)',
    color: '#1e3a8a',
    border: '1px solid rgba(147,197,253,0.7)',
    borderRadius: '999px',
    padding: '5px 10px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.2px',
  },

  quickActionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '14px',
  },

  quickActionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '16px',
    borderRadius: '18px',
    border: '1px solid var(--border-color)',
    background: 'var(--bg-quick-action)',
    cursor: 'pointer',
  },

  quickActionIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    background: 'var(--bg-quick-action-icon)',
    color: '#4338ca',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 700,
    flexShrink: 0,
  },

  quickActionTitle: {
    fontSize: '15px',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: '4px',
  },

  quickActionText: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    lineHeight: 1.5,
  },

  upcomingListWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    height: '268px',
    overflowY: 'hidden',
    paddingRight: '2px',
    transition: 'all 0.25s ease',
  },

  upcomingListWrapExpanded: {
    height: '268px',
    overflowY: 'auto',
    paddingRight: '4px',
  },

  upcomingItem: {
    position: 'relative',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    background: 'var(--bg-card)',
    padding: '10px 12px 10px 14px',
    minHeight: '100px',
    boxShadow: '0 6px 16px rgba(15,23,42,0.08)',
  },

  upcomingStrip: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '4px',
    borderTopLeftRadius: '12px',
    borderBottomLeftRadius: '12px',
    background: 'linear-gradient(180deg, #22d3ee 0%, #2563eb 100%)',
    boxShadow: '0 0 0 1px rgba(37,99,235,0.08) inset',
  },

  upcomingItemTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px',
  },

  upcomingTopActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
  },

  upcomingIdentity: {
    minWidth: 0,
  },

  upcomingCandidate: {
    fontSize: '14px',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: '2px',
    lineHeight: 1.2,
  },

  upcomingMeta: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    overflowWrap: 'anywhere',
  },

  upcomingStatus: {
    fontSize: '10px',
    fontWeight: 800,
    padding: '4px 10px',
    borderRadius: '999px',
    whiteSpace: 'nowrap',
    border: '1px solid transparent',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },

  upcomingScheduleRow: {
    marginTop: '6px',
    display: 'inline-flex',
    gap: '6px',
    alignItems: 'center',
    borderRadius: '999px',
    padding: '3px 8px',
    background: '#ecfeff',
    border: '1px solid #67e8f9',
  },

  upcomingScheduleLabel: {
    fontSize: '9px',
    color: '#0e7490',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },

  upcomingScheduleValue: {
    fontSize: '12px',
    color: '#0f172a',
    fontWeight: 700,
  },

  upcomingLinksRow: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginTop: '6px',
  },

  upcomingPillLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 16px',
    borderRadius: '999px',
    fontSize: '13px',
    fontWeight: 700,
    textDecoration: 'none',
    transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
    cursor: 'pointer',
    border: '1px solid transparent',
  },
  candidatePill: {
    background: '#6366f1',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
  },
  interviewerPill: {
    background: '#fff',
    color: '#4338ca',
    border: '1px solid #c7d2fe',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
  },
  copyIconButtonCandidate: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '5px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.4)',
    background: 'rgba(255, 255, 255, 0.15)',
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  copyIconButtonInterviewer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '5px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    background: '#f8fafc',
    color: '#64748b',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  upcomingLinkAnchor: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#1d4ed8',
    textDecoration: 'none',
    background: '#eff6ff',
    border: '1px solid #93c5fd',
    padding: '4px 9px',
    borderRadius: '999px',
    boxShadow: '0 2px 8px rgba(37,99,235,0.14)',
  },

  upcomingDeleteBtnCompact: {
    padding: '4px 10px',
    borderRadius: '9px',
    border: '1px solid #fca5a5',
    background: '#fee2e2',
    color: '#dc2626',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '10px',
  },

  upcomingFooterActions: {
    marginTop: '6px',
    display: 'flex',
    justifyContent: 'center',
  },

  upcomingViewMoreBtn: {
    border: '1px solid rgba(147,197,253,0.7)',
    background: 'linear-gradient(135deg, rgba(219,234,254,0.95) 0%, rgba(191,219,254,0.95) 100%)',
    color: '#1e3a8a',
    borderRadius: '999px',
    padding: '7px 16px',
    fontWeight: 700,
    fontSize: '11px',
    cursor: 'pointer',
    boxShadow: '0 8px 18px rgba(59,130,246,0.2)',
  },

  overviewList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    maxHeight: '62vh',
    overflowY: 'auto',
    paddingRight: '6px',
  },

  overviewItem: {
    border: '1px solid var(--border-light)',
    borderRadius: '16px',
    padding: '10px 12px',
    background: 'var(--bg-card)',
    boxShadow: 'var(--shadow-card)',
  },

  overviewItemTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },

  overviewIdentity: {
    minWidth: 0,
  },

  overviewCandidate: {
    fontSize: '16px',
    fontWeight: 800,
    color: 'var(--text-primary)',
    marginBottom: '3px',
    lineHeight: 1.2,
  },

  overviewMeta: {
    fontSize: '12px',
    color: 'var(--text-tertiary)',
    overflowWrap: 'anywhere',
  },

  overviewScheduleRow: {
    marginTop: '7px',
    display: 'inline-flex',
    gap: '6px',
    alignItems: 'center',
    borderRadius: '999px',
    padding: '4px 8px',
    background: '#eef2ff',
    border: '1px solid #c7d2fe',
  },

  overviewScheduleLabel: {
    fontSize: '10px',
    color: '#4338ca',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },

  overviewScheduleValue: {
    fontSize: '11px',
    color: 'var(--text-primary)',
    fontWeight: 700,
  },

  overviewLinksRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '8px',
  },

  overviewLinkAnchor: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#1d4ed8',
    textDecoration: 'none',
    background: '#eff6ff',
    border: '1px solid #93c5fd',
    padding: '5px 10px',
    borderRadius: '999px',
    boxShadow: '0 4px 10px rgba(59,130,246,0.18)',
  },

  overviewStatus: {
    fontSize: '10px',
    fontWeight: 800,
    padding: '4px 8px',
    borderRadius: '999px',
    whiteSpace: 'nowrap',
    border: '1px solid transparent',
    textTransform: 'uppercase',
    letterSpacing: '0.35px',
  },

  overviewActions: {
    marginTop: '8px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '6px',
    paddingTop: '8px',
    borderTop: '1px solid var(--border-color)',
  },

  linkActionBtn: {
    padding: '6px 10px',
    borderRadius: '9px',
    border: '1px solid #93c5fd',
    background: '#dbeafe',
    color: '#1d4ed8',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '11px',
  },

  deleteActionBtn: {
    padding: '6px 10px',
    borderRadius: '9px',
    border: '1px solid #fca5a5',
    background: '#fee2e2',
    color: '#b91c1c',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '11px',
  },

  emptyOverview: {
    border: '1px dashed var(--border-color)',
    borderRadius: '12px',
    padding: '18px',
    textAlign: 'center',
    color: 'var(--text-muted)',
    fontSize: '13px',
    background: 'var(--bg-surface)',
  },

  overviewModalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'var(--bg-modal-overlay)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },

  overviewModalCard: {
    width: 'min(920px, 100%)',
    maxHeight: '84vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    background: 'var(--bg-modal-card)',
    border: '1px solid var(--border-modal)',
    borderRadius: '24px',
    boxShadow: 'var(--shadow-lg)',
    padding: '22px',
  },

  overviewModalHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    paddingBottom: '10px',
    borderBottom: '1px solid #dbeafe',
  },

  modalCloseBtn: {
    padding: '8px 14px',
    borderRadius: '12px',
    border: '1px solid var(--border-color)',
    background: 'var(--bg-card)',
    color: 'var(--text-secondary)',
    fontWeight: 700,
    cursor: 'pointer',
  },

  summaryTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },

  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    paddingBottom: '12px',
    borderBottom: '1px solid #eef2f7',
  },

  summaryLabel: {
    fontSize: '13px',
    color: '#64748b',
  },

  summaryValue: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#0f172a',
  },

  insightCard: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderRadius: '24px',
    padding: '20px',
    boxShadow: 'var(--shadow-card)',
  },

  insightCardSoft: {
    background: 'linear-gradient(135deg, #eff6ff 0%, #eef2ff 100%)',
    border: '1px solid #dbeafe',
    borderRadius: '24px',
    padding: '20px',
  },

  insightHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '16px',
  },

  smallDateChip: {
    fontSize: '11px',
    color: 'var(--text-tertiary)',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-color)',
    padding: '6px 10px',
    borderRadius: '999px',
  },

  insightList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },

  insightItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },

  insightDotBlue: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: '#4f46e5',
    flexShrink: 0,
  },

  insightDotOrange: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: '#f59e0b',
    flexShrink: 0,
  },

  insightDotGreen: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: '#10b981',
    flexShrink: 0,
  },

  insightTextWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },

  insightItemTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: 'var(--text-primary)',
  },

  insightItemSub: {
    fontSize: '12px',
    color: 'var(--text-muted)',
  },

  stepList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },

  stepItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },

  stepNo: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: '#eef2ff',
    color: '#4338ca',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 800,
    flexShrink: 0,
  },

  stepTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: '4px',
  },

  stepText: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    lineHeight: 1.5,
  },

  softCardLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#6366f1',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    marginBottom: '8px',
  },

  softCardTitle: {
    fontSize: '18px',
    fontWeight: 800,
    color: 'var(--text-primary)',
    marginBottom: '8px',
  },

  softCardText: {
    fontSize: '13px',
    color: 'var(--text-muted)',
    lineHeight: 1.6,
  }, linkSection: {
    marginTop: '25px',
  },

  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    marginBottom: '15px',
    color: '#0f172a'
  },

  linkCard: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderRadius: '16px',
    padding: '15px',
    marginBottom: '15px',
    boxShadow: 'var(--shadow-card)'
  },

  linkCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px'
  },

  avatarCircle: {
    width: '35px',
    height: '35px',
    borderRadius: '50%',
    background: '#6366f1',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold'
  },

  linkCardTitle: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '600'
  },

  linkCardSubtitle: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--text-muted)'
  },

  linkInputWrapper: {
    display: 'flex',
    width: '100%',
    gap: '0',
    marginTop: '10px'
  },

  copyButton: {
    padding: '10px 14px',
    background: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px'
  },
  modernTitle: {
    padding: '40px',
    paddingBottom: '10px',
    marginLeft: '-38px'

  }

  /* Keep your existing modal/calendar/link styles below this line */
};

export default RoomSelection;